import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, TextField, IconButton, Typography, CircularProgress, Avatar } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { getJSON, postJSON } from '../../utils/api';

export default function ChatWindow({ conversation }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [text, setText] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Fetch peer's profile picture
  useEffect(() => {
    const fetchProfilePicture = async () => {
      const peerId = conversation.peer?.id || conversation.peer?._id;
      if (!peerId) return;

      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('rv_access_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
        
        const response = await fetch(`${API_BASE}/api/profile/user/${peerId}/picture-url`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setProfilePictureUrl(data.url);
        }
      } catch (error) {
        console.error('Failed to fetch profile picture:', error);
      }
    };

    fetchProfilePicture();
  }, [conversation.peer]);

  const loadMessages = useCallback(async () => {
    try {
      const data = await getJSON(`/api/chats/${conversation.id}/messages?limit=50`);
      setMessages(data || []);
    } catch (e) {
      setErr('Failed to load messages.');
    } finally {
      setLoading(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [conversation.id]);

  useEffect(() => {
    setMessages([]);
    setLoading(true);
    setErr(null);
    loadMessages();
    const t = setInterval(loadMessages, 15000);
    return () => clearInterval(t);
  }, [loadMessages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const msg = await postJSON(`/api/chats/${conversation.id}/messages`, { text: text.trim() });
      setMessages(prev => [...prev, msg]);
      setText('');
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const getAvatarLetter = () => {
    const name = conversation.peer?.name || conversation.peer?.username || 'U';
    return name[0].toUpperCase();
  };

  return (
    <Box 
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Header with Profile Picture */}
      <Box 
        sx={{
          p: 2,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          flexShrink: 0,
          backgroundColor: '#fff',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Avatar
          src={profilePictureUrl || undefined}
          sx={{
            width: 40,
            height: 40,
            bgcolor: '#667eea',
          }}
        >
          {!profilePictureUrl && getAvatarLetter()}
        </Avatar>
        <Typography variant="subtitle1" fontWeight="bold">
          {conversation.peer?.name || conversation.peer?.username || 'Chat'}
        </Typography>
      </Box>

      {/* Messages Container - SCROLLABLE */}
      <Box 
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 2,
          backgroundColor: '#f9f9f9',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress size={24} />
          </Box>
        ) : err ? (
          <Typography color="error" variant="body2">{err}</Typography>
        ) : messages.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="body2" color="text.secondary">
              No messages yet. Say hello 👋
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map(m => (
              <Box 
                key={m.id} 
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: m.isMine ? 'flex-end' : 'flex-start'
                }}
              >
                <Box
                  sx={{
                    borderRadius: 2,
                    padding: '8px 12px',
                    background: m.isMine ? '#667eea' : '#fff',
                    color: m.isMine ? '#fff' : '#000',
                    maxWidth: '70%',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                >
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {m.text}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {new Date(m.createdAt).toLocaleString()}
                </Typography>
              </Box>
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </Box>

      {/* Input Box - FIXED AT BOTTOM */}
      <Box 
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          borderTop: '1px solid rgba(0,0,0,0.08)',
          backgroundColor: '#fff',
          flexShrink: 0,
          zIndex: 1
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={3}
          placeholder="Type a message…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={onKeyDown}
          variant="outlined"
          size="small"
        />
        <IconButton 
          onClick={send} 
          disabled={sending || !text.trim()}
          sx={{
            backgroundColor: '#667eea',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#5568d3'
            },
            '&:disabled': {
              backgroundColor: '#ccc'
            }
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
